import { getShapeLayers, getShapeTop } from './shapes';
import { resolvePresetValue } from '../../utils/helpers';

/**
 * The divider's SVG, identical in the editor and in saved markup.
 *
 * Decorative by definition — it carries no information a reader could miss —
 * so it is hidden from assistive tech rather than given a label nobody wants
 * read aloud.
 *
 * GRADIENTS
 * ---------
 * An SVG `fill` cannot take a CSS gradient, so a gradient is a real
 * `<linearGradient>` in the document rather than a custom property. That means
 * a document-wide id, and two dividers sharing one would both take whichever
 * definition rendered last — hence `uniqueId`, stored on the block. The
 * alternative, masking a gradient-filled box with the shape, would need every
 * path duplicated as a CSS data URI and the catalogue would exist twice.
 *
 * @param {Object} props            Component props.
 * @param {Object} props.attributes Block attributes.
 * @return {Element} SVG element.
 */
export default function ShapeSvg({ attributes }) {
  const { shape, fillType, gradientFrom, gradientTo, gradientAngle, uniqueId } =
    attributes;

  const isGradient = fillType === 'gradient';
  const gradientId = `brandy-shape-${uniqueId || 'default'}`;

  // Unset stops fall through to the stylesheet's custom properties, which point
  // at the theme's primary colour — a divider inserted into a Brandy site is
  // then in the site's colour, not a hard-coded blue. `resolvePresetValue`
  // turns a palette pick (`var:preset|color|…`) into real CSS; without it a
  // chosen preset reached `stop-color` verbatim and painted nothing.
  //
  // Set through `style` rather than the `stop-color` attribute: `var()` inside
  // an SVG presentation attribute is parsed inconsistently across browsers,
  // while the CSS property is not.
  const stopStyle = (value, property) => ({
    stopColor: value ? resolvePresetValue(value) : `var(${property})`,
  });

  // A solid fill is left to the stylesheet, which is the only way round: CSS
  // beats SVG presentation attributes, so a `path { fill }` rule that applied
  // to every divider would silently defeat every gradient below it.
  const fill = isGradient ? `url(#${gradientId})` : undefined;

  // Crop the box to where the shape actually draws. Left uncropped, a taller
  // divider mostly grows the empty band above the ink — the default waves fill
  // barely half the 1200x120 box — and the shape looks cut off rather than
  // taller.
  const top = getShapeTop(shape);

  return (
    <svg
      className="brandy-shape-divider__svg"
      viewBox={`0 ${top} 1200 ${120 - top}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {isGradient && (
        <defs>
          <linearGradient
            id={gradientId}
            // Rotating about the middle of the box keeps the angle meaningful
            // whatever the divider's proportions: a gradient rotated about its
            // origin would slide off a wide strip entirely.
            gradientTransform={`rotate(${gradientAngle}, 0.5, 0.5)`}
          >
            <stop
              offset="0%"
              style={stopStyle(gradientFrom, '--brandy-shape-divider-from')}
            />
            <stop
              offset="100%"
              style={stopStyle(gradientTo, '--brandy-shape-divider-to')}
            />
          </linearGradient>
        </defs>
      )}

      {getShapeLayers(shape).map((layer, index) => (
        <path
          key={index}
          d={layer.d}
          fill={fill}
          fillOpacity={layer.opacity === 1 ? undefined : layer.opacity}
        />
      ))}
    </svg>
  );
}
